import { MAX_COMPARE_SLOTS } from "@/features/enterprise-proposals-compare/_logic/constants";
import { meaningfulTokens, toWords, tokenMatches } from "./match";

/** Compare needs at least two venues to be a comparison at all. */
export const MIN_COMPARE_VENUES = 2;

/**
 * Toggles a venue in the compare selection.
 *
 * Selection is by `venueStaticId` rather than by proposal, because the compare
 * page renders one column per venue: picking two offers from the same hotel
 * would silently collapse to one column. Deduping here keeps the palette's
 * count honest.
 *
 * @param selected - Currently selected venue ids, in selection order.
 * @param venueStaticId - The venue to add or remove.
 * @returns The next selection, capped at {@link MAX_COMPARE_SLOTS}.
 */
export function toggleVenue(selected: number[], venueStaticId: number): number[] {
  if (selected.includes(venueStaticId)) {
    return selected.filter((id) => id !== venueStaticId);
  }
  if (selected.length >= MAX_COMPARE_SLOTS) return selected;
  return [...selected, venueStaticId];
}

/**
 * Whether a selection can be compared.
 *
 * @param selected - Currently selected venue ids.
 * @returns true once there are at least two venues.
 */
export const canCompare = (selected: number[]): boolean => selected.length >= MIN_COMPARE_VENUES;

/**
 * Builds the compare URL for a selection.
 *
 * The route calls `notFound()` when `venues` is empty, so this returns null
 * rather than a bare path: a link that 404s is worse than no link, and that is
 * exactly the bug this page exists to fix.
 *
 * @param eventId - The event whose compare page to open, or null if unresolved.
 * @param selected - Selected venue ids.
 * @returns The URL, or null when it cannot be built.
 */
export function buildCompareHref(eventId: string | null, selected: number[]): string | null {
  // Takes null rather than an empty-string fallback: `${""}` produced
  // `/dashboard/source//proposals/compare`, which was unreachable only because
  // venues cannot load without an event. Guard structurally, not by luck.
  if (!eventId) return null;
  if (!canCompare(selected)) return null;
  return `/dashboard/source/${eventId}/proposals/compare?venues=${selected.join(",")}`;
}

/**
 * Helper text under the input, so the user knows why Enter is not firing yet.
 *
 * @param selected - Currently selected venue ids.
 * @returns A short status string.
 */
export function selectionHint(selected: number[]): string {
  if (selected.length === 0) return `Select ${MIN_COMPARE_VENUES} or more to compare`;
  if (selected.length === 1) return "Select one more to compare";
  if (selected.length >= MAX_COMPARE_SLOTS) return `${selected.length} selected, the maximum`;
  return `${selected.length} selected`;
}

/**
 * Narrows the compare list to what the user typed.
 *
 * The palette turns cmdk's own filter off and ranks the root list itself, which
 * left every nested page unfiltered: the box said "Select two or more
 * proposals..." and then ignored every character typed into it. Worse on the
 * event picker, where the highlighted row stayed put while the query changed,
 * so Enter opened an event the user had not named.
 *
 * Matches on name and location with the same primitives as `rank.ts`, so one
 * query means one thing across the whole palette.
 *
 * @param venues - Every comparable venue for the event.
 * @param query - The raw query string. Empty returns `venues` untouched.
 * @returns The venues worth showing, in their original order.
 */
export function filterVenues<T extends { venueName: string; location: string | null }>(
  venues: T[],
  query: string
): T[] {
  const tokens = meaningfulTokens(query);
  if (tokens.length === 0) return venues;

  return venues.filter((venue) => {
    const words = toWords([venue.venueName, venue.location ?? ""].join(" "));
    return tokens.every((token) => tokenMatches(words, token));
  });
}
